import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CTAButton, CTAGroup } from '../components/CTAButtons';
import { AnimatedVideo } from '../components/AnimatedVideo';
import { Testimonials } from '../components/Testimonials';
import { TrustBadges } from '../components/TrustBadges';
import { CheckIcon, ShieldIcon, LockIcon, ArrowRightIcon, TrendingUpIcon } from 'lucide-react';
export const Home: React.FC = () => {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const staggerChildren = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const pricingPackages = [{
    title: 'Basic Business Exit',
    price: 'POA',
    features: ['Simple company transfer', 'Limited debts (under £50k)', 'No major creditor issues', 'Standard documentation', '30-day completion target']
  }, {
    title: 'Urgent Rescue',
    price: 'POA',
    recommended: true,
    features: ['Expedited company transfer', 'Medium debts (£50k-£150k)', 'Some creditor arrears', 'Priority documentation', '14-day completion target']
  }, {
    title: 'Complex Rescue',
    price: 'POA',
    features: ['Comprehensive transfer', 'Significant debts (£150k+)', 'Substantial creditor issues', 'Complex documentation', 'Tailored completion timeline']
  }];
  return <div className="bg-white" data-id="element-302">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-gray-900 to-black text-white" data-id="element-303">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80')] bg-cover bg-center" data-id="element-304"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" data-id="element-305"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32" data-id="element-306">
          <motion.div className="max-w-3xl" initial="hidden" animate="visible" variants={fadeIn} data-id="element-307">
            <div className="inline-flex items-center bg-red-600/20 border border-red-600/30 rounded-full px-4 py-2 mb-6" data-id="element-308">
              <TrendingUpIcon className="h-4 w-4 mr-2 text-red-400" data-id="element-309" />
              <span className="text-sm text-red-200" data-id="element-310">
                Limited availability - Contact us today
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-311">
              Struggling With Business Debt? We Help You Hand It Over Safely.
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300" data-id="element-312">
              We match distressed Ltd companies with buyers willing to take over
              debts, liabilities, and challenges so you can move forward.
            </p>
            <CTAGroup className="mb-8" showPriority={true} data-id="element-313" />
            <div className="flex items-center text-sm text-gray-300" data-id="element-314">
              <LockIcon className="h-4 w-4 mr-2" data-id="element-315" />
              <span data-id="element-316">Completely confidential, discreet service</span>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Trust Badges */}
      <section className="py-12 bg-gray-50 border-b border-gray-200" data-id="element-317">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-318">
          <TrustBadges data-id="element-319" />
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white" data-id="element-320">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-321">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-322">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-323">
              How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-324">
              Our process is straightforward and designed to help you exit your
              troubled business quickly and efficiently.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-325">
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-326">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-327">
                1
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-328">Consultation</h3>
              <p className="text-gray-600" data-id="element-329">
                We assess your company's situation, including debts,
                liabilities, and assets to determine the best approach.
              </p>
            </motion.div>
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-330">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-331">
                2
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-332">Buyer Matching</h3>
              <p className="text-gray-600" data-id="element-333">
                We source a qualified buyer willing to take over your company
                with its existing liabilities and challenges.
              </p>
            </motion.div>
            <motion.div className="bg-gray-50 p-8 rounded-lg text-center" variants={fadeIn} data-id="element-334">
              <div className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6" data-id="element-335">
                3
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-336">Transfer & Exit</h3>
              <p className="text-gray-600" data-id="element-337">
                We facilitate the legal transfer of your company, allowing you
                to walk away from the business and its debts.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Who We Help */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-338">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-340">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-341">Who We Help</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-342">
              We specialise in helping Ltd company owners facing these
              challenging situations:
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-2 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-343">
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-344">
              <div className="mr-4" data-id="element-345">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-346">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-347" />
                </div>
              </div>
              <div data-id="element-348">
                <h3 className="text-lg font-bold mb-2" data-id="element-349">
                  Mounting Business Debt
                </h3>
                <p className="text-gray-600" data-id="element-350">
                  Companies struggling with suppliers, creditors, and mounting
                  financial obligations that are becoming unmanageable.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-351">
              <div className="mr-4" data-id="element-352">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-353">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-354" />
                </div>
              </div>
              <div data-id="element-355">
                <h3 className="text-lg font-bold mb-2" data-id="element-356">
                  Loans, Utilities & Unsecured Debt
                </h3>
                <p className="text-gray-600" data-id="element-357">
                  Businesses burdened with business loans, utility arrears, and
                  unsecured debt that's creating cash flow problems and mounting
                  pressure from creditors.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-358">
              <div className="mr-4" data-id="element-359">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-360">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-361" />
                </div>
              </div>
              <div data-id="element-362">
                <h3 className="text-lg font-bold mb-2" data-id="element-363">Lease Commitments</h3>
                <p className="text-gray-600" data-id="element-364">
                  Companies tied into expensive property or equipment leases
                  that are draining cash flow and creating ongoing liabilities.
                </p>
              </div>
            </motion.div>
            <motion.div className="bg-white p-6 rounded-lg shadow-sm flex" variants={fadeIn} data-id="element-365">
              <div className="mr-4" data-id="element-366">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-367">
                  <CheckIcon className="h-5 w-5 text-red-600" data-id="element-368" />
                </div>
              </div>
              <div data-id="element-369">
                <h3 className="text-lg font-bold mb-2" data-id="element-370">Insolvency Risk</h3>
                <p className="text-gray-600" data-id="element-371">
                  Businesses at risk of insolvency proceedings who need a more
                  controlled exit strategy before formal proceedings begin.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white" data-id="element-372">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-373">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-374">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-375">
              Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-376">
              Real business owners who found their way forward (names withheld
              for confidentiality)
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-377">
            <Testimonials data-id="element-378" />
          </motion.div>
        </div>
      </section>
      {/* Video Explainer */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-379">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-380">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-381">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-382">
              See How It Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-383">
              Watch our 30-second explainer to understand how we can help you
              move forward from business debt.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-384">
            <AnimatedVideo data-id="element-385" />
          </motion.div>
        </div>
      </section>
      {/* Pricing At A Glance */}
      <section className="py-16 md:py-24 bg-white" data-id="element-386">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-387">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-388">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-389">
              Pricing At A Glance
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-390">
              Transparent pricing based on the complexity of your situation
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-391">
            {pricingPackages.map((pkg, index) => <motion.div key={index} className={`border rounded-lg overflow-hidden ${pkg.recommended ? 'border-red-600 shadow-lg' : 'border-gray-200'}`} variants={fadeIn} data-id="element-392">
                {pkg.recommended && <div className="bg-red-600 text-white text-center py-1 text-sm font-medium" data-id="element-393">
                    Recommended
                  </div>}
                <div className="p-6" data-id="element-394">
                  <h3 className="text-xl font-bold mb-2" data-id="element-395">{pkg.title}</h3>
                  <p className="text-3xl font-bold mb-4" data-id="element-396">{pkg.price}</p>
                  <ul className="space-y-3 mb-6" data-id="element-397">
                    {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-398">
                        <CheckIcon className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-399" />
                        <span className="text-gray-700" data-id="element-400">{feature}</span>
                      </li>)}
                  </ul>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-401">
                    Contact us for a personalised quote
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
          <motion.div className="text-center mt-12" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-402">
            <Link to="/services" data-id="element-403">
              <button className="inline-flex items-center px-6 py-3 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors" data-id="element-404">
                <span data-id="element-405">View Full Pricing Details</span>
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-406" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
      {/* Trust Statements */}
      <section className="py-16 md:py-24 bg-black text-white" data-id="element-407">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-408">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-409">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-410">
              Our Commitment
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto" data-id="element-411">
              We understand the sensitive nature of business financial
              difficulties.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerChildren} data-id="element-412">
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-413">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-414">
                <LockIcon className="h-6 w-6" data-id="element-415" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-416">Confidentiality</h3>
              <p className="text-gray-300" data-id="element-417">
                All discussions and information shared are treated with absolute
                confidentiality and discretion.
              </p>
            </motion.div>
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-418">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-419">
                <ShieldIcon className="h-6 w-6" data-id="element-420" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-421">Protection</h3>
              <p className="text-gray-300" data-id="element-422">
                We work to protect your personal reputation and financial
                standing through proper company transfer.
              </p>
            </motion.div>
            <motion.div className="bg-gray-900 p-8 rounded-lg" variants={fadeIn} data-id="element-423">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mb-6" data-id="element-424">
                <CheckIcon className="h-6 w-6" data-id="element-425" />
              </div>
              <h3 className="text-xl font-bold mb-4" data-id="element-426">Transparency</h3>
              <p className="text-gray-300" data-id="element-427">
                Clear, straightforward advice and pricing with no hidden fees or
                unexpected charges.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-white" data-id="element-428">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-429">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-430">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-431">
              Ready to Hand Over Your Business Challenges?
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-432">
              Contact us today for a confidential discussion about your
              situation.
            </p>
            <CTAGroup className="justify-center" showPriority={true} data-id="element-433" />
            <p className="text-sm text-gray-500 mt-6" data-id="element-434">
              Average response time: 24 hours | 100% confidential
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Credit */}
      <section className="py-8 bg-gray-50 border-t border-gray-200" data-id="element-435">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-436">
          <motion.div className="flex flex-col items-center justify-center" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-437">
            <a href="https://bright-pixxel.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-4 hover:opacity-80 transition-opacity" data-id="element-438">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pkCFBj5bhhYQqeVubg29ef/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio" className="h-12 w-auto" data-id="element-439" />
              <span className="text-base text-gray-600" data-id="element-440">
                Created by{' '}
                <span className="font-semibold text-gray-900" data-id="element-441">
                  Bright Pixxel Studio
                </span>
              </span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
};